import type { SiteConfig } from '@/stores';

/** True when the persisted selection is a key in `getDistinctTypesBySite`. */
export const isSelectedSiteIdInTypesBySite = (
  selectedSiteId: string | undefined,
  typesBySite: Record<string, unknown> | undefined
): boolean =>
  selectedSiteId != null && typesBySite != null && Object.hasOwn(typesBySite, selectedSiteId);

type ResolveAppCurrentSiteProps<T extends Pick<SiteConfig, 'id'>> = {
  persist: boolean;
  persistSiteId?: string;
  getPersistedSite: (id: string) => T | undefined;
  portableMode: boolean;
  selectedSiteId?: string;
  typesBySite: Record<string, unknown> | undefined;
  areTypesBySiteFetched: boolean;
  sites: readonly T[];
  defaultSite: T | undefined;
};

/**
 * App-level site: in portable mode a stored id must appear as a `getDistinctTypesBySite` key,
 * otherwise treat it as empty (first visit). Nested `persist` providers still resolve from
 * `persistSiteId`. Edge uses the resolved site list, whose id is the inventory site id, and
 * falls back to the bootstrap default site only when that list is empty.
 */
export const resolveAppCurrentSite = <T extends Pick<SiteConfig, 'id'>>({
  persist,
  persistSiteId,
  getPersistedSite,
  portableMode,
  selectedSiteId,
  typesBySite,
  areTypesBySiteFetched,
  sites,
  defaultSite,
}: ResolveAppCurrentSiteProps<T>): T | undefined => {
  if (persist && persistSiteId) return getPersistedSite(persistSiteId);

  if (!portableMode) {
    return sites.find(site => site.id === selectedSiteId) ?? sites[0] ?? defaultSite;
  }

  if (!selectedSiteId) return undefined;

  if (areTypesBySiteFetched && !isSelectedSiteIdInTypesBySite(selectedSiteId, typesBySite)) {
    return undefined;
  }

  return sites.find(site => site.id === selectedSiteId);
};

type ShouldRequireSiteSelectionProps<T> = {
  persist: boolean;
  portableMode: boolean;
  areSitesResolved: boolean;
  sites: readonly T[];
  currentSite: T | undefined;
};

/**
 * Force the picker whenever the app-level provider has sites but resolved none of them. Deliberately
 * independent of the types-by-site query: that query can only reject a selection, never turn an
 * unresolvable one into a site, so waiting on it (or on a request that failed) would leave the page
 * blank with no way to pick a site.
 */
export const shouldRequireSiteSelection = <T>({
  persist,
  portableMode,
  areSitesResolved,
  sites,
  currentSite,
}: ShouldRequireSiteSelectionProps<T>): boolean =>
  !persist && portableMode && areSitesResolved && sites.length > 0 && currentSite == null;
